/**
 * The campaign hierarchy an event carries: a tree of SFDC campaign ids.
 *
 * The tree and the operations on it live here, apart from the component, because they are
 * the part worth reasoning about on their own — inserting and removing at arbitrary depth
 * is where this kind of editor usually goes wrong, and it is testable without rendering.
 *
 * Nodes hold a campaign id. The tree is persisted as the service's campaign-node resources
 * (one per node, see hierarchySync.ts); a node loaded from or saved to the service carries its
 * `nodeId`, which is how a save tells an existing node from a new one.
 *
 * Every function here is pure and returns a NEW tree. Nothing mutates in place, so React
 * sees a changed reference and a stale closure cannot write into an old tree.
 */

/** A node in the hierarchy. `children` is always an array; empty for a leaf. */
export interface HierarchyNode {
  /**
   * Identity for editing only: React keys, which row to expand, which node an action
   * targets. Generated in the browser and NOT part of the saved shape — the tree's
   * meaning is the campaign ids and their nesting, and persisting a local key would
   * invite someone to treat it as a server id.
   */
  key: string;
  /** The SFDC campaign id. Empty while a freshly added row is still blank. */
  sfdcCampaignId: string;
  children: HierarchyNode[];
  /** The service's id for this node, once it exists there. Absent for a row added here. */
  nodeId?: string;
  /**
   * Set for a node that is another Event Hub event placed under this one (`LIFE_CYCLE_EVENT`),
   * rather than a bare SFDC campaign. Its campaign id belongs to that event, so it is read-only
   * here, and a chosen event is never dropped for being blank.
   *
   * The empty string marks an event row whose event is not chosen yet: it is an event row (see
   * isEventRow) but blank, so it is flagged and pruned like an empty campaign row.
   */
  eventId?: string;
}

/** Which kind of row a marketer adds: a typed SFDC campaign id, or another Event Hub event. */
export type NodeKind = 'campaign' | 'event';

/** Whether a row is an event row — chosen or still to be chosen — rather than a campaign row. */
export function isEventRow(node: HierarchyNode): boolean {
  return node.eventId !== undefined;
}

let nextKey = 0;

/** A key unique within this page load, which is all `key` has to be. */
export function newKey(): string {
  nextKey += 1;
  return `h${nextKey}`;
}

export function newNode(sfdcCampaignId = ''): HierarchyNode {
  return { key: newKey(), sfdcCampaignId, children: [] };
}

/** A row of the given kind, still blank: no campaign id typed, or no event chosen. */
export function newNodeOfKind(kind: NodeKind): HierarchyNode {
  return kind === 'event' ? { ...newNode(), eventId: '' } : newNode();
}

/**
 * Applies `fn` to the node with `key`, rebuilding only the path down to it.
 *
 * The single recursion behind every edit below: returning `undefined` from `fn` removes
 * the node. Untouched subtrees keep their identity, so React re-renders the branch that
 * changed rather than the whole tree.
 */
function mapNode(
  nodes: readonly HierarchyNode[],
  key: string,
  fn: (node: HierarchyNode) => HierarchyNode | undefined,
): HierarchyNode[] {
  let changed = false;
  const out: HierarchyNode[] = [];
  for (const node of nodes) {
    if (node.key === key) {
      changed = true;
      const next = fn(node);
      if (next) out.push(next);
      continue;
    }
    const children = mapNode(node.children, key, fn);
    if (children === node.children) {
      out.push(node);
    } else {
      changed = true;
      out.push({ ...node, children });
    }
  }
  /* The original array when nothing below it changed — that is what lets an untouched
     branch keep its identity all the way up. Returning `out` unconditionally would rebuild
     every level on every keystroke, which is the bug a test here caught.

     The cast is safe: nothing in this module writes to the array, and `readonly` on the
     parameter is a promise to callers rather than a different runtime type. */
  return changed ? out : (nodes as HierarchyNode[]);
}

/**
 * Chooses the event on an event row. `sfdcCampaignId` is that event's own id, copied for display
 * and the duplicate check only — the service derives the node's id from the event, so it is
 * never sent.
 */
export function setEvent(
  nodes: readonly HierarchyNode[],
  key: string,
  eventId: string,
  sfdcCampaignId: string,
): HierarchyNode[] {
  return mapNode(nodes, key, (node) => ({ ...node, eventId, sfdcCampaignId }));
}

/** Sets one node's campaign id. */
export function setCampaignId(
  nodes: readonly HierarchyNode[],
  key: string,
  sfdcCampaignId: string,
): HierarchyNode[] {
  return mapNode(nodes, key, (node) => ({ ...node, sfdcCampaignId }));
}

/** Appends an empty child of `kind` to one node. Returns the new tree and the child's key. */
export function addChild(
  nodes: readonly HierarchyNode[],
  parentKey: string,
  kind: NodeKind = 'campaign',
): { nodes: HierarchyNode[]; childKey: string } {
  const child = newNodeOfKind(kind);
  return {
    nodes: mapNode(nodes, parentKey, (node) => ({
      ...node,
      children: [...node.children, child],
    })),
    childKey: child.key,
  };
}

/**
 * Removes a node AND its descendants.
 *
 * Deliberately not re-parenting the children onto the removed node's parent: a campaign
 * hierarchy is a containment structure, so a child that survives its parent would be
 * claiming a place in the tree nobody asked for. The component warns when the node being
 * removed is not a leaf.
 */
export function removeNode(
  nodes: readonly HierarchyNode[],
  key: string,
): HierarchyNode[] {
  return mapNode(nodes, key, () => undefined);
}

/**
 * Whether any node beneath `node` exists in the service. Such a node blocks deleting `node`: the
 * service refuses to delete a node with children.
 */
export function hasSavedChildren(node: HierarchyNode): boolean {
  return node.children.some((c) => !!c.nodeId || hasSavedChildren(c));
}

/** How many nodes the subtree rooted at each of `nodes` holds, including themselves. */
export function countNodes(nodes: readonly HierarchyNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0);
}

/**
 * The campaign ids that appear more than once, anywhere in the tree.
 *
 * A campaign id identifies one campaign, so the same id in two places is a contradiction
 * about where that campaign sits rather than two things that happen to match. Reported
 * rather than prevented, so the marketer can see both offenders and decide which is wrong.
 */
export function duplicateCampaignIds(nodes: readonly HierarchyNode[]): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  const walk = (list: readonly HierarchyNode[]) => {
    for (const node of list) {
      const id = node.sfdcCampaignId.trim();
      if (id) {
        if (seen.has(id)) dupes.add(id);
        seen.add(id);
      }
      walk(node.children);
    }
  };
  walk(nodes);
  return dupes;
}

/** Keys of nodes whose campaign id is still blank. */
export function blankKeys(nodes: readonly HierarchyNode[]): Set<string> {
  const keys = new Set<string>();
  const walk = (list: readonly HierarchyNode[]) => {
    for (const node of list) {
      if (!node.eventId && !node.sfdcCampaignId.trim()) keys.add(node.key);
      walk(node.children);
    }
  };
  walk(nodes);
  return keys;
}

/**
 * Drops blank nodes, so a row the marketer added and left empty does not get saved.
 *
 * A blank node with children keeps them, lifted to where it was: the children carry real
 * campaign ids, and discarding them because their parent row was never filled in would
 * throw away work that is visibly on screen. This is the one place children are
 * re-parented, and it happens on the way out rather than as an edit.
 */
export function pruneBlank(nodes: readonly HierarchyNode[]): HierarchyNode[] {
  return nodes.flatMap((node) => {
    const children = pruneBlank(node.children);
    // An event node's campaign id is the event's own, possibly unset; it is not a blank row.
    if (!node.eventId && !node.sfdcCampaignId.trim()) return children;
    return [{ ...node, children }];
  });
}

/**
 * The root row's label: "CampaignId (Event Name)", so the marketer can see the hierarchy's parent
 * IS the SFDC Campaign ID entered above it (testing doc #37). Just the name while no id is set.
 */
export function rootLabel(campaignId: string | undefined, eventName: string | undefined): string {
  const id = campaignId?.trim();
  const name = eventName?.trim() || 'this event';
  return id ? `${id} (${name})` : name;
}

/** Every event placed anywhere in the tree. */
export function eventIdsInTree(nodes: readonly HierarchyNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (list: readonly HierarchyNode[]) => {
    for (const node of list) {
      if (node.eventId) ids.add(node.eventId);
      walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

/** An event the picker can list. */
export interface PickableEvent {
  eventId?: string;
  eventName?: string;
  sfdcCampaignId?: string;
}

/** One picker option: "Event Name - CampaignId", valued by event id. */
export interface EventOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
}

export const NO_CAMPAIGN_ID_HINT = 'Configure the SFDC Campaign ID on this event first';

/**
 * The events that can be placed under `selfEventId`, as picker options.
 *
 * Left out: the event itself, events already in this (unsaved) tree, and every event that already
 * backs a hierarchy node anywhere (`withNodes`, see eventIdsWithNodes) — an event backs at most
 * one node, and this editor only ever creates fresh ones. That last set also holds this event's
 * own ancestors when it sits in another tree, which is what keeps a pick from making a cycle.
 *
 * An event with no SFDC Campaign ID is listed but disabled: it would be a node with no campaign
 * to roll up, and the fix is on that event, so the option says so.
 */
export function eventOptions(
  events: readonly PickableEvent[],
  selfEventId: string,
  inTree: ReadonlySet<string>,
  withNodes: ReadonlySet<string>,
): EventOption[] {
  return events
    .filter(
      (e): e is PickableEvent & { eventId: string } =>
        !!e.eventId &&
        e.eventId !== selfEventId &&
        !inTree.has(e.eventId) &&
        !withNodes.has(e.eventId),
    )
    .map((e) => {
      const name = e.eventName?.trim() || e.eventId;
      const id = e.sfdcCampaignId?.trim();
      return id
        ? { value: e.eventId, label: `${name} - ${id}` }
        : {
            value: e.eventId,
            label: `${name} - (no campaign ID)`,
            disabled: true,
            description: NO_CAMPAIGN_ID_HINT,
          };
    })
    .sort((a, b) => Number(!!a.disabled) - Number(!!b.disabled) || a.label.localeCompare(b.label));
}
